import React from 'react';
import { Link } from 'react-router-dom';
import { useInView } from 'framer-motion';
import { useTilt } from '../hooks/useTilt';
import { useCountUp } from '../motion';

export type ProofCount = { from: number; to: number; ms: number; prefix?: string; suffix?: string };

/* The headline number counts up once, the first time the tile is on screen.
   Reduced motion gets the final string with no counter at all. */
function CountValue({ count, run }: { count: ProofCount; run: boolean }) {
    const n = useCountUp(count.to, { from: count.from, duration: count.ms, run });
    return (
        <>
            {count.prefix}
            {n}
            {count.suffix}
        </>
    );
}

export function ProofTile({
    value,
    unit,
    label,
    to,
    count,
}: {
    value: string;
    unit?: string;
    label: string;
    to?: string;
    count?: ProofCount;
}) {
    const tilt = useTilt<HTMLElement>({ max: 6, lift: 18 });
    const seen = React.useRef<HTMLSpanElement>(null);
    const inView = useInView(seen, { once: true, margin: '-10% 0px -10% 0px' });

    const body = (
        <>
            <div className="ds-proof__row">
                <span className="ds-proof__value" ref={seen}>
                    {count ? <CountValue count={count} run={inView} /> : value}
                </span>
                {unit && <span className="ds-proof__unit">{unit}</span>}
            </div>
            <p className="ds-proof__label">{label}</p>
        </>
    );

    if (!to) {
        return (
            <div
                ref={tilt.ref as React.RefObject<HTMLDivElement>}
                onPointerMove={tilt.onPointerMove}
                onPointerLeave={tilt.onPointerLeave}
                className="ds-proof ds-glass ds-glass--dark ds-glass--r-xl ds-proof--static"
            >
                {body}
            </div>
        );
    }

    return (
        <Link
            ref={tilt.ref as React.RefObject<HTMLAnchorElement>}
            onPointerMove={tilt.onPointerMove}
            onPointerLeave={tilt.onPointerLeave}
            to={to}
            className="ds-proof ds-glass ds-glass--dark ds-glass--r-xl"
        >
            {body}
            <span className="ds-proof__foot">
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M4.5 11.5 11.5 4.5M5.5 4.5h6v6" />
                </svg>
            </span>
        </Link>
    );
}
